/**
 * Confirmation dialog shown when saving an edited interview.
 * Shows which invites/emails will be sent.
 *
 * 3 variants based on what changed:
 * - invites — updated calendar invites only
 * - emails — updated notification emails only
 * - invites-and-emails — both groups changed
 */
import { Flex, Box } from '@chakra-ui/react';
import { Modal, Avatar, AvatarGroup, Text } from '@kula-ai/components';
import { SCHEDULE_INTERVIEW_COPY } from '../config/copy';

export interface RecipientInfo {
  name: string;
  avatar?: string;
}

export interface SaveConfirmationProps {
  /** What type of updates are being sent. */
  variant: 'invites' | 'emails' | 'invites-and-emails';
  /** Candidate receiving updated invites/emails. */
  candidate: RecipientInfo;
  /** Whether the candidate is in the invite recipients list. */
  showCandidateInvite?: boolean;
  /** Whether the interviewer(s) are in the invite recipients list. */
  showInterviewerInvite?: boolean;
  /** Whether the candidate is in the email recipients list. */
  showCandidateEmail?: boolean;
  /** Whether the interviewer(s) are in the email recipients list. */
  showInterviewerEmail?: boolean;
  /** Interviewers receiving updated invites. */
  interviewerInviteRecipients?: RecipientInfo[];
  /** Interviewers receiving updated emails. */
  interviewerEmailRecipients?: RecipientInfo[];
  /** Called when user confirms. */
  onConfirm: () => void;
  /** Called when user cancels — dialog dismissed, drawer stays open. */
  onCancel: () => void;
  isLoading?: boolean;
}

export const SaveConfirmationDialog = ({
  variant,
  candidate,
  showCandidateInvite = true,
  showInterviewerInvite = true,
  showCandidateEmail = true,
  showInterviewerEmail = true,
  interviewerInviteRecipients = [],
  interviewerEmailRecipients = [],
  onConfirm,
  onCancel,
  isLoading = false,
}: SaveConfirmationProps) => {
  const copy = SCHEDULE_INTERVIEW_COPY.editDialogs;

  const title =
    variant === 'invites'
      ? copy.saveInvites.title
      : variant === 'emails'
        ? copy.saveEmails.title
        : copy.saveInvitesAndEmails.title;

  const showInvitesSection =
    variant === 'invites' || variant === 'invites-and-emails';
  const showEmailsSection =
    variant === 'emails' || variant === 'invites-and-emails';

  return (
    <Modal
      open
      onOpenChange={(open) => {
        if (!open && !isLoading) onCancel();
      }}
      title={title}
      overlayZIndex={1450}
      contentZIndex={1500}
      primaryButton={{
        label: SCHEDULE_INTERVIEW_COPY.footer.saveInterview,
        onClick: onConfirm,
        isLoading,
      }}
      secondaryButton={{
        label: SCHEDULE_INTERVIEW_COPY.footer.cancel,
        onClick: onCancel,
        disabled: isLoading,
      }}
    >
      <Flex
        flexDir="column"
        bg="secondary-50"
        borderRadius="xs"
        border="1px solid"
        borderColor="secondary-100"
      >
        {showInvitesSection && (
          <RecipientBox
            label={copy.saveInvites.invitesLabel}
            candidate={showCandidateInvite ? candidate : undefined}
            interviewers={
              showInterviewerInvite ? interviewerInviteRecipients : []
            }
          />
        )}

        {showEmailsSection && (
          <RecipientBox
            label={copy.saveEmails.emailsLabel}
            candidate={showCandidateEmail ? candidate : undefined}
            interviewers={
              showInterviewerEmail ? interviewerEmailRecipients : []
            }
          />
        )}
      </Flex>
    </Modal>
  );
};

function RecipientBox({
  label,
  candidate,
  interviewers,
}: {
  label: string;
  candidate?: RecipientInfo;
  interviewers: RecipientInfo[];
}) {
  const hasInterviewers = interviewers.length > 0;

  return (
    <Box px="4" py="3">
      <Text variant="p" color="heading" className="mb-3">
        {label}
      </Text>
      <Flex align="center" gap="6">
        {candidate && (
          <Flex align="center" gap="1" flex="1" minW="0">
            <Text variant="p" color="heading" className="pr-1">
              {SCHEDULE_INTERVIEW_COPY.editDialogs.candidateLabel}
            </Text>
            <Avatar
              size="sm"
              shape="square"
              name={candidate.name}
              src={candidate.avatar}
            />
            <Text variant="p" truncate tooltip className="min-w-0 flex-1">
              {candidate.name}
            </Text>
          </Flex>
        )}

        {hasInterviewers && (
          <Flex align="center" gap="1" flex="1" minW="0">
            <Text variant="p" color="heading" className="pr-1">
              {SCHEDULE_INTERVIEW_COPY.editDialogs.interviewersLabel}
            </Text>
            {interviewers.length === 1 ? (
              <>
                <Avatar
                  size="sm"
                  shape="square"
                  name={interviewers[0].name}
                  src={interviewers[0].avatar}
                />
                <Text variant="p" truncate tooltip className="min-w-0 flex-1">
                  {interviewers[0].name}
                </Text>
              </>
            ) : (
              <AvatarGroup size="sm" max={3}>
                {interviewers.map((r) => (
                  <Avatar
                    shape="square"
                    key={r.name}
                    name={r.name}
                    src={r.avatar}
                  />
                ))}
              </AvatarGroup>
            )}
          </Flex>
        )}
      </Flex>
    </Box>
  );
}
